import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography, Stack, Chip, Tooltip } from '@mui/material';
import Iconify from '../../../../components/iconify';
import { friendlyNameConverter } from '../../../../services/localizationService';
import { extractValues, pearsonCorrelation, spearmanCorrelation, linearRegression } from '../statistics';
import { useTimelineControls } from '../TimelineContext';

function escapeHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export default function CorrelationWidget({ recordings = [], widgetConfig }) {
  const { window: timeWindow } = useTimelineControls();
  const recId = widgetConfig?.recId || '';
  const sensorKeyX = widgetConfig?.sensorKeyX || '';
  const sensorKeyY = widgetConfig?.sensorKeyY || '';

  const rec = recordings.find((r) => r.id === recId);

  const { scatterData, pearson, spearman, regressionLine } = useMemo(() => {
    if (!rec || !sensorKeyX || !sensorKeyY) return { scatterData: null, pearson: null, spearman: null, regressionLine: null };
    const xs = extractValues(rec, sensorKeyX, timeWindow?.start, timeWindow?.end);
    const ys = extractValues(rec, sensorKeyY, timeWindow?.start, timeWindow?.end);
    const n = Math.min(xs.length, ys.length);
    const data = Array.from({ length: n }, (_, i) => [xs[i], ys[i]]);

    // Compute regression line from scatter points
    let regLine = null;
    if (n >= 2) {
      const points = Array.from({ length: n }, (_, i) => ({ x: xs[i], y: ys[i] }));
      const reg = linearRegression(points);
      if (reg) {
        let xMin = xs[0], xMax = xs[0];
        for (let i = 1; i < n; i++) { if (xs[i] < xMin) xMin = xs[i]; if (xs[i] > xMax) xMax = xs[i]; }
        regLine = {
          data: [[xMin, reg.slope * xMin + reg.intercept], [xMax, reg.slope * xMax + reg.intercept]],
          r2: reg.r2,
        };
      }
    }

    return {
      scatterData: data,
      pearson: pearsonCorrelation(xs, ys),
      spearman: spearmanCorrelation(xs, ys),
      regressionLine: regLine,
    };
  }, [rec, sensorKeyX, sensorKeyY, timeWindow]);

  const nameX = friendlyNameConverter(sensorKeyX || '');
  const nameY = friendlyNameConverter(sensorKeyY || '');

  const chartSeries = [
    { name: 'Data', type: 'scatter', data: scatterData || [] },
    ...(regressionLine ? [{ name: 'Regression', type: 'line', data: regressionLine.data }] : []),
  ];

  const chartOptions = {
    chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: true }, animations: { enabled: false } },
    xaxis: { title: { text: nameX }, tickAmount: 5 },
    yaxis: { title: { text: nameY }, tickAmount: 5 },
    markers: { size: [3, 0], opacity: 0.6 },
    stroke: { width: [0, 2], dashArray: [0, 4], curve: 'straight' },
    colors: ['#2196F3', '#FF5722'],
    legend: { show: false },
    tooltip: {
      shared: false,
      intersect: true,
      custom: ({ seriesIndex, dataPointIndex }) => {
        if (seriesIndex !== 0 || !scatterData?.[dataPointIndex]) return '';
        const [x, y] = scatterData[dataPointIndex];
        return `<div style="padding:6px">${escapeHtml(nameX)}: <b>${x.toFixed(2)}</b><br/>${escapeHtml(nameY)}: <b>${y.toFixed(2)}</b></div>`;
      },
    },
    grid: { padding: { left: 10, right: 10 } },
  };

  const correlationTooltipText =
    'Pearson r measures linear correlation \u2014 best for normally distributed data. ' +
    'Spearman \u03c1 measures monotonic correlation \u2014 robust to outliers and non-normal distributions. ' +
    'If values differ significantly, the relationship may be non-linear.';

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {scatterData && pearson ? (
        <Box sx={{ flex: 1, overflow: 'hidden', px: 1 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
            <Chip label={`Pearson r=${pearson.r.toFixed(3)} (${pearson.interpretation})`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
            <Chip label={`Spearman \u03c1=${spearman.rho.toFixed(3)}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
            {regressionLine && (
              <Chip label={`R\u00b2=${regressionLine.r2.toFixed(3)}`} size="small" variant="outlined" color="warning" sx={{ height: 18, fontSize: '0.6rem' }} />
            )}
            {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
            <Tooltip title={correlationTooltipText} arrow placement="top">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}>
                <Iconify icon="eva:info-fill" width={14} sx={{ color: 'text.disabled' }} />
              </Box>
            </Tooltip>
          </Stack>
          <ReactApexChart type="line" series={chartSeries} options={chartOptions} height="85%" />
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.55rem', lineHeight: 1.2, px: 0.5 }}>
            <Iconify icon="eva:alert-triangle-fill" width={10} sx={{ verticalAlign: 'middle', mr: 0.3, color: 'warning.main' }} />
            Note: Sensor capping, initial ramp-up periods, and small sample sizes can skew correlation values. Correlation does not imply causation.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">Select a recording and two sensors</Typography>
        </Box>
      )}
    </Box>
  );
}
